import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import './App.css';
import Button from 'devextreme-react/button';
import { getCompanySettings } from './utils/apiHelpers';
import { generatePaystubPDF, generatePaystubWithLogo } from './utils/pdfHelpers';

const API_BASE = 'https://qbo.omnivistaweb.com';


const PaystubPage = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const [paystub, setPaystub] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [salaryMemo1, setSalaryMemo1] = useState<string>('');

  useEffect(() => {
    document.body.style.overflowY = 'auto';
    document.documentElement.style.overflowY = 'auto';
  
    return () => {
      document.body.style.overflowY = '';
      document.documentElement.style.overflowY = '';
    };
  }, []);
  

  useEffect(() => {
    const fetchPaystub = async () => {
      try {
        const res = await axios.get(`https://qbo.omnivistaweb.com/api/paystub-by-txnid`, {
          params: { txnid: employeeId }
        });

        const rows = res.data;
        if (!rows.length) {
          setError('No paystub data found.');
          return;
        }

        const name = rows[0].display_name;
        const date = rows[0].date;
        const governmentId = rows[0].government_id;
        const email = rows[0].email || 'unknown@example.com';

        const earnings = rows.map((r: any) => ({
          type: r.account,
          amount: parseFloat(r.amount || 0),
          amount_ytd: parseFloat(r.amount_ytd || 0)
        }));

        const total = earnings.reduce((sum: number, e: any) => sum + e.amount, 0);
        const ytdTotal = earnings.reduce((sum: number, e: any) => sum + e.amount_ytd, 0);

        setPaystub({
          name,
          netPay: total,
          grossPay: total,
          netPayYtd: ytdTotal,
          grossPayYtd: ytdTotal,
          governmentId,
          email,
          earnings,
          payPeriod: {
            start: date,
            end: date,
            paidOn: date
          },
          timeOff: {
            vacation: 160,
            sick: 40
          }
        });
        
      } catch (err) {
        console.error(err);
        setError('Failed to load paystub.');
      }
    };

    fetchPaystub();
  }, [employeeId]);

  useEffect(() => {
    const fetchCompany = async () => {
      const company = await getCompanySettings();
      setSalaryMemo1(company.company_salary_memo_1 || '');
    };
    fetchCompany();
  }, []);

  const formatAmount = (amount: number) =>
    amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /*
  const exportToPDF = async () => {
    try {
      console.log('📥 exportToPDF clicked');
      if (!paystub) return;
      const company = await getCompanySettings();

      console.log('🏢 Company settings:', company);
      console.log('🔗 Company Logo URL:', company.company_logo);
      const logoUrl = `https://qbo.omnivistaweb.com${company.company_logo}`;
      const doc = await generatePaystubWithLogo(paystub, formatAmount, logoUrl, company.company_name,company.company_salary_memo1);
    
      const fileName = `Paystub_txnid_${employeeId}.pdf`;
      console.log('📄 Saving PDF1 as:', fileName);
      doc.save(fileName);
    } catch (err) {
      console.error('❌ Failed to export PDF:', err);
    }
  };
  */
  const exportToPDF = async () => {
    try {
      console.log('📥 exportToPDF clicked');
      if (!paystub) return;
      const company = await getCompanySettings();
      const logoUrl = `https://qbo.omnivistaweb.com${company.company_logo}`;
      const doc = await generatePaystubWithLogo(paystub, formatAmount, logoUrl, company.company_name, company.company_salary_memo_1);
  
      // Convert to Blob
      const pdfBlob = doc.output('blob');
  
      // Prepare to send to backend for encryption
      const formData = new FormData();
      formData.append('pdf', pdfBlob, 'paystub.pdf');
      formData.append('txnid', String(employeeId)); // 🔐 Send txnid for lookup to iban
  
      // Send to your encryption endpoint
      const response = await fetch('https://qbo.omnivistaweb.com/api/encrypt-and-download', {
        method: 'POST',
        body: formData
      });
  
      if (!response.ok) {
        throw new Error('Server failed to encrypt PDF');
      }
  
      const encryptedBlob = await response.blob();
      const fileName = `Paystub_txnid_${employeeId}.pdf`;
  
      // Trigger file download
      const url = window.URL.createObjectURL(encryptedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      console.log('✅ Encrypted PDF downloaded');
    } catch (err) {
      console.error('❌ Failed to export encrypted PDF:', err);
      alert('Failed to generate encrypted PDF.');
    }
  };
  
  const sendEmail = async () => {
    console.log('📧 sendEmail clicked');
    if (!paystub) return;
    const company = await getCompanySettings();
    const logoUrl = `https://qbo.omnivistaweb.com${company.company_logo}`;
    const doc = await generatePaystubWithLogo(paystub, formatAmount, logoUrl, company.company_name, company.company_salary_memo_1);

    const blob = doc.output('blob');
  
    const formData = new FormData();
    formData.append('pdf', blob, `paystub_${employeeId}.pdf`);
    formData.append('txnid', String(employeeId));
  
    try {
      await axios.post('https://qbo.omnivistaweb.com/api/send-pdf-from-client', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('📧 Email sent!');
    } catch (err) {
      console.error(err);
      alert('❌ Failed to send email.');
    }
  };
    
  

  if (error) return <div style={{ padding: 20, color: 'red' }}>{error}</div>;
  if (!paystub) return <div style={{ padding: 20 }}>Loading...</div>;

  return (
    <div className="paystub-container">
      <h2 className="paystub-title">Paystub for {paystub.name}</h2>

      <div className="paystub-section">
        <div><strong>Government ID:</strong> {paystub.governmentId}</div>
        <div><strong>Email:</strong> {paystub.email}</div>
        <div><strong>Pay Period:</strong> {paystub.payPeriod.start} to {paystub.payPeriod.end}</div>
        <div><strong>Paid On:</strong> {paystub.payPeriod.paidOn}</div>
      </div>

      <div className="paystub-row">
        <div className="pay-card">
          <h3>Net Pay</h3>
          <div className="pay-amount">AED {formatAmount(paystub.netPay)}</div>
        </div>
        <div className="pay-card alt">
          <h3>Net Pay (YTD)</h3>
          <div className="pay-amount">AED {formatAmount(paystub.netPayYtd)}</div>
        </div>
      </div>

      <div className="paystub-section">
        <h3>Earnings</h3>
        <table className="earnings-table">
          <thead>
            <tr>
              <th>Type</th>
              <th style={{ textAlign: 'right' }}>This Period (AED)</th>
              <th style={{ textAlign: 'right' }}>Year to Date (AED)</th>
            </tr>
          </thead>
          <tbody>
            {paystub.earnings.map((e: any, idx: number) => (
              <tr key={idx}>
                <td>{e.type}</td>
                <td style={{ textAlign: 'right' }}>{formatAmount(e.amount)}</td>
                <td style={{ textAlign: 'right' }}>{formatAmount(e.amount_ytd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="paystub-section">
        <h3>Available Time Off</h3>
        <div className="paystub-row">
          <div className="timeoff-box"><strong>Vacation:</strong> {paystub.timeOff.vacation} hrs</div>
          <div className="timeoff-box"><strong>Sick:</strong> {paystub.timeOff.sick} hrs</div>
        </div>
      </div>

      {salaryMemo1 && (
        <div style={{
          marginTop: 20,
          background: '#f5f5f5',
          padding: '10px 15px',
          borderRadius: 6,
          color: '#333',
          fontSize: 13,
          maxHeight: 60,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'pre-wrap'
        }}>
          {salaryMemo1}
        </div>
      )}

      <div className="paystub-actions">
        <Button icon="exportpdf" text="Export to PDF" type="default" stylingMode="contained" onClick={exportToPDF} />
        <Button icon="email" text="Email" type="default" stylingMode="contained" onClick={sendEmail} style={{ marginLeft: 10 }} />
      </div>

    </div>
  );
};

export default PaystubPage;
